import { useEffect, useMemo, useRef, useState } from "react";
import isEmpty from "lodash/isEmpty";
import capitalize from "lodash/capitalize";
import { useSelector } from "react-redux";

import { useProductDetailContext } from "@contexts/product-detail-context";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import { getIsPricelessShop } from "@selectors/store";

import BsDropdown from "@stadium/ui/src/components/dropdown/bs-dropdown";

import { isSnackOrDSDProduct, isSwagProduct } from "@helpers/products/types";

import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { ANALYTICS_EVENT_ACTION_SELECTED } from "@stadium/services/analytics/constants/default";
import { ANALYTICS_TARGET_ATTRIBUTE } from "@constants/analytics/shop";

import { OPTIONTYPE_SIZE_KEY, ORDER_IN_BULK_OPTION } from "@constants/products/detail";

import styles from "./main.module.scss";

const ProductOptionSelector = ({
  title,
  availData = [],
  handleChange,
  intent,
  selectedData,
  disabled = false,
  hasTierPricing = false,
  isColorPresent,
  errorClassName = "",
  dropdownClassName = " ",
  selectBulkOptionInEditMode,
  isSingleSize = false,
  eventLocationAnalytics = {},
}) => {
  const {
    isOptionSelected,
    optionSelectionError: showErrorMessage,
    setIsOptionSelected,
  } = useProductDetailContext();
  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();

  const withAnalytics = useWithAnalytics();

  const isPricelessShop = useSelector(getIsPricelessShop);

  const [selectedOption, setSelectedOption] = useState(null);
  const hasAutoSelectedRef = useRef(false);

  const availDataKey = availData.map(({ id }) => id).join(",");

  useEffect(() => {
    hasAutoSelectedRef.current = false;
  }, [availDataKey]);

  // empty selected size if selected data changed
  useEffect(() => {
    setSelectedOption(selectedData);
    if (!isEmpty(selectedData)) {
      setIsOptionSelected(true);
    }
  }, [selectedData, setIsOptionSelected]);

  const isVariantsSelectionNotRequired = () => {
    return isSnackOrDSDProduct(intent) && availData.length <= 1;
  };
  const renderPriceOrPackLabelContent = (data, isHead) => {
    const isActive = data.id === selectedOption?.id;
    let className = styles["labelContentWrap"];
    className += isActive ? " activeBg " : " ";
    className += data.outOfStock ? styles.eshopOutOfStockLabel : " ";

    return (
      <div className={className}>
        <span className={styles.dataAmount}>{data.presentation}</span>
        {!OPTIONTYPE_SIZE_KEY.includes(data.optionTypeKey) && (
          <span className={styles.amountIn}>{formatPriceInDisplayStoreCurrency(data.priceIn)}</span>
        )}
        {!isHead && data.outOfStock && (
          <span className={styles.eshopsOutOfStock}>Out of Stock</span>
        )}
      </div>
    );
  };

  const dropdownOptions = useMemo(() => {
    const updatedAvailData =
      hasTierPricing && !isEmpty(availData) && !isSingleSize
        ? [...availData, ORDER_IN_BULK_OPTION]
        : availData;
    return updatedAvailData.map((data) => ({
      key: `${data.name}-${data.id}-${data.variantId}`,
      label: renderPriceOrPackLabelContent(data),
      data,
    }));
  }, [availData, hasTierPricing, selectedOption]);

  const dropDownInitialValue = useMemo(() => {
    if (dropdownOptions.length === 1) {
      return 0;
    }

    if (selectBulkOptionInEditMode && isEmpty(selectedData)) {
      return dropdownOptions.length - 1;
    }

    if (!isEmpty(selectedData) && selectedData?.id !== selectedOption?.id) {
      return availData.findIndex((opt) => opt.id === selectedData?.id);
    }

    const currentIndex = availData.findIndex((opt) => opt.id === selectedOption?.id);

    return currentIndex === -1 ? null : currentIndex;
  }, [selectedData, selectedOption, selectBulkOptionInEditMode, dropdownOptions, availData]);

  useEffect(() => {
    if (dropdownOptions.length === 1) {
      setIsOptionSelected(true);
    }
  }, [dropdownOptions.length, setIsOptionSelected]);

  const dropDownOptionClickHandler = (selectedVal, track = true) => {
    setIsOptionSelected(true);
    setSelectedOption(selectedVal.data);

    if (track && eventLocationAnalytics.track) {
      withAnalytics(undefined, {
        ...eventLocationAnalytics,
        target: eventLocationAnalytics?.target ?? ANALYTICS_TARGET_ATTRIBUTE,
        action: eventLocationAnalytics?.action ?? ANALYTICS_EVENT_ACTION_SELECTED,
        eventProperties: {
          product_id: eventLocationAnalytics.eventProperties?.product_id,
          attribute_type: eventLocationAnalytics.eventProperties?.attribute_type,
          attribute_value: selectedVal.data.presentation,
          ...eventLocationAnalytics?.eventProperties,
        },
      })();
    }

    return handleChange(selectedVal.data);
  };

  useEffect(() => {
    if (hasAutoSelectedRef.current) return;

    //for tier-pricing non-locker shop selecting Order in bulk option if `editProductIds` size > 1
    if (selectBulkOptionInEditMode && isEmpty(selectedData)) {
      hasAutoSelectedRef.current = true;
      dropDownOptionClickHandler({ data: ORDER_IN_BULK_OPTION }, false);
    }
  }, [selectBulkOptionInEditMode, selectedData, dropdownOptions]);

  if (
    (!isSwagProduct(intent) && (availData.length === 0 || isPricelessShop)) ||
    isVariantsSelectionNotRequired()
  )
    return null;

  const updatedTitle = typeof title === "string" ? capitalize(title) : title;

  return (
    <div className={`${styles.mb16} rdSelector`}>
      <div className={styles.title}>{updatedTitle}</div>
      <BsDropdown
        id="dropdown-basic"
        initValue={dropDownInitialValue}
        placeHolder="Select"
        options={dropdownOptions}
        renderHeadValue={(option) => renderPriceOrPackLabelContent(option?.data, true)}
        onChange={dropDownOptionClickHandler}
        customClass={styles.bsDropdown}
        toggleBtnClass={`${styles.rdDropdownToggle} ${
          (showErrorMessage && !isOptionSelected) || selectedData.outOfStock
            ? styles["btnError"]
            : ""
        }`}
        disabled={disabled}
        dropdownClass={`${styles.sizeDropdownMenu} ${dropdownClassName}`}
        toggleArrowClass={styles.iconChevronDown}
      />
      {showErrorMessage && !isOptionSelected && (
        <span className={`${styles.addToBagBtnError} ${errorClassName}`}>
          Please select a value
        </span>
      )}
      {selectedData.outOfStock && (
        <span className={`${styles.addToBagBtnError} ${errorClassName}`}>
          {`Out of stock, please select another size${isColorPresent ? " or color" : ""}`}
        </span>
      )}
    </div>
  );
};

export default ProductOptionSelector;
